
import { mapGetters } from 'vuex'
import type { RawLocation } from 'vue-router'
import PSidebar from '@invivodf/common/src/application/presenters/PSidebar/PSidebar.vue'
import PBoToast from '@invivodf/common/src/application/presenters/PBoToast/PBoToast.vue'
import { computed, defineComponent, ref } from 'vue'
import {
  checkFeature,
  DynamicFeaturesEnum,
  EnvFeaturesEnum,
  FeatureType,
} from '@invivodf/front-seller-feature-flipping'
import { provideToastNotification } from '@invivodf/common/src/application/composables'
import PSelect from '@/contexts/menu/application/presenters/PSelect/PSelect.vue'
import { useNavigationLinks } from '@/contexts/menu/application/composables'
import { useBrandData } from '@/contexts/menu/application/composables/useBrandData'
import { useStore } from '@/composables/useStore'

export default defineComponent({
  name: 'VLayoutMain',
  components: { PBoToast, PSidebar, PSelect },
  setup() {
    provideToastNotification()

    const hasOpenSidebar = ref(false)

    const { brandData } = useBrandData()

    const store = useStore()
    const { isPartnerWithNewOfferConfiguration, hasBrandManagePermission, hasProductManagerManagePermission } =
      store.getters
    const isCompaniesFeatureEnabled = computed(() => window.env.COMPANIES_FEATURE === 'yes')
    const isRedirectionsLinkVisible = computed<boolean>(
      () => window.env.NEW_REDIRECT_FEATURE === 'yes' && Boolean(hasBrandManagePermission),
    )
    // Synchronous read, as in useMetaDiscountFeature: the menu is built once, and useFeatureFlip
    // would resolve after that build.
    const isOrdersToValidateFeatureEnabled = computed<boolean>(
      () => window.env[EnvFeaturesEnum.ORDERS_TO_VALIDATE_FEATURE] === 'yes',
    )
    const isLogisticConfigFeatureEnabled = computed<boolean>(
      () => window.env[EnvFeaturesEnum.LOGISTIC_CONFIG_FEATURE] === 'yes',
    )
    // Izanami flag, checked per distributor: unlike the env flags above, it resolves after the
    // first build, so the menu is rebuilt once it answers.
    const isProductManagementFeatureEnabled = ref(false)
    checkFeature
      .execute({
        id: DynamicFeaturesEnum.PRODUCT_MANAGEMENT,
        type: FeatureType.Dynamic,
        context: { id: String(store.getters.cooperativeId) },
      })
      .then((isEnabled) => {
        isProductManagementFeatureEnabled.value = isEnabled
      })

    const navigationLinks = computed(
      () =>
        useNavigationLinks(
          isPartnerWithNewOfferConfiguration,
          isCompaniesFeatureEnabled.value,
          isRedirectionsLinkVisible.value,
          isOrdersToValidateFeatureEnabled.value,
          isLogisticConfigFeatureEnabled.value,
          isProductManagementFeatureEnabled.value && Boolean(hasProductManagerManagePermission),
        ).navigationLinks,
    )
    function onSidebarOpen(value: boolean): void {
      hasOpenSidebar.value = value
    }
    async function onLogout() {
      await store.dispatch('logout')
    }
    return {
      hasOpenSidebar,
      brandData,
      navigationLinks,
      onSidebarOpen,
      onLogout,
    }
  },
  data() {
    return { remountCount: 0, remountOnRoute: null as string | null }
  },
  computed: {
    ...mapGetters(['toast']),
    // Sibling routes rendering the same component (every VMicroFrontProvider route, e.g. `orders` and
    // `orders-to-validate`) would otherwise share one instance: Vue only swaps the id and src of the
    // live iframe, which pushes a joint-history entry, and Back then rewinds the iframe but not the
    // host URL. Keying by route record (matched[0] is this layout) mounts a fresh child per record,
    // while a param change within one record (e.g. `webhooks/:webhookId`) keeps the instance.
    childRouteKey(): string | undefined {
      const recordPath = this.$route.matched[1]?.path
      return recordPath === undefined ? undefined : `${recordPath}#${this.remountCount}`
    },
  },
  methods: {
    // A micro-front that navigates inside its iframe (list → detail) only rewrites the host URL:
    // `syncParentUrl` replaces the history entry, the host route stays the one it entered on. A menu
    // click on that route must still show it afresh, so the child is remounted on the URL the
    // navigation leaves behind. A menu link to another route record mounts a new child anyway.
    onNavigate(link: RawLocation | undefined): void {
      if (!link) return
      const target = this.$router.resolve(link).route
      if (target.matched[1]?.path !== this.$route.matched[1]?.path) return
      if (target.fullPath === this.$route.fullPath) {
        // Duplicated navigation: vue-router aborts it after restoring the host URL, synchronously.
        this.remountCount += 1
      } else {
        // Tied to the target: vue-router 3 runs no hook when the navigation is aborted, and a later,
        // unrelated one must not take the remount over.
        this.remountOnRoute = target.fullPath
      }
    },
  },
  watch: {
    $route(route) {
      const { remountOnRoute } = this
      this.remountOnRoute = null
      if (route.fullPath === remountOnRoute) this.remountCount += 1
    },
    toast(toast) {
      this.$bvToast.toast(toast.message, {
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 3000,
        headerClass: 'd-none',
        bodyClass: `toast__${toast.type}`,
      })
    },
  },
})
